import styled, { createGlobalStyle } from 'styled-components';

/**
 * CSS-variable bridge between Paradigm and `@tradeparadigm/terminal-react`.
 * The terminal (and its settings sub-view) style themselves entirely through
 * these variables.
 *
 * They're defined on `:root`, NOT a scoped wrapper, on purpose: the terminal
 * portals popups (the account menu, the model picker) to `document.body` via
 * `createPortal`, so a scoped wrapper's variables never reach them and they
 * render unthemed — transparent background, near-invisible text. `:root` is
 * inherited by portaled content too. This matches how the package's own
 * standalone app themes itself (ui/terminal/app/src/theme.css defines the
 * same variables on `:root`).
 *
 * Safe to define globally: these names are consumed only by terminal-owned
 * surfaces in this app (the embedded terminal, `SettingsBlockTerminal`, the
 * terminal slot in `BottomPanel`) — no general Paradigm styling reads them.
 * Values mirror the standalone terminal's dark theme so the embed renders
 * identically to the deployed standalone.
 */
export const TerminalThemeVars = createGlobalStyle`
  :root {
    --text-primary: #e8e8e8;
    --text-secondary: #b9bbbd;
    --text-label: #aab0b6;
    --bg-color-page: #090a0a;
    --bg-color-container: #16191c;
    --border-color: #302f38;
    --divider-color: color-mix(in srgb, #282b2e 50%, transparent);
    --input-bg-color: color-mix(in srgb, #282b2e 75%, transparent);
    --accent-color: #1bc1cf;
    --error-color: #e3415a;
    --warning-color: #ffcb23;
    --pending-color: #ff9800;
    --success-color: #3fb950;
    --accent-color-rgb: 27, 193, 207;
    --error-color-rgb: 227, 65, 90;
    --warning-color-rgb: 255, 203, 35;
    --success-color-rgb: 63, 185, 80;
    --pending-color-rgb: 255, 152, 0;
    --color-lightest: 255, 255, 255;
    --cta-text-color: #090a0a;
    --hover-bg: rgba(255, 255, 255, 0.05);
    --button-color: #42474d;
    --button-color-dark: #282b2e;
    --font-mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    --chat-body-max-width: 900px;
    --chat-input-max-width: 770px;
  }
`;

/**
 * Layout wrapper for the in-drawer `<Terminal>`. Variables come from
 * `TerminalThemeVars` (global); this only handles sizing so the terminal
 * fills the drawer/side-panel content area.
 */
const TerminalThemeScope = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  /* The package's Root fills via height: 100%. */
  & > * {
    flex: 1;
    min-height: 0;
  }
`;

export default TerminalThemeScope;
